import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Play, Send, Upload, Trash2 } from "lucide-react";
import { api, errMsg, downloadFile, viewFile } from "../../lib/api";
import { dateBR, dateTimeBR } from "../../lib/format";
import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { FileLine } from "../TreatParts";

export const fileLine = (d, onRemove) => (
  <div key={d.id} className="flex items-center gap-2">
    <div className="min-w-0 flex-1"><FileLine name={d.filename} sub={dateTimeBR(d.created_at)} testid={`folha-file-${d.id}`} onView={() => viewFile(`/documents/${d.id}/download`)} onDownload={() => downloadFile(`/documents/${d.id}/download`, d.filename)} /></div>
    {onRemove && <Button size="icon" variant="ghost" className="h-9 w-9 text-rose-600" onClick={() => onRemove(d)} aria-label="Remover" data-testid={`folha-return-remove-${d.id}`}><Trash2 className="h-4 w-4" /></Button>}
  </div>);

export function StartPanel({ r, onDone }) {
  const [days, setDays] = useState(2);
  const [due, setDue] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (days >= 1 && days <= 60) api.get("/calendar/preview", { params: { days } }).then((x) => setDue(x.data.date)).catch(() => setDue(null));
    else setDue(null);
  }, [days]);
  const start = async () => {
    setBusy(true);
    try { await api.post(`/folha/requests/${r.id}/start`, { business_days: days }); toast.success("Processamento iniciado. O cliente já vê a previsão."); onDone(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <section className="space-y-3 rounded-xl border border-sky-200 bg-sky-50/50 p-4" data-testid="folha-start-panel">
      <div className="label-xs">Prazo de devolução</div>
      <div className="flex flex-wrap items-center gap-2">{[1, 2, 3, 4, 5].map((n) => (
        <button key={n} type="button" onClick={() => setDays(n)} className={`h-10 min-w-10 rounded-lg border px-3 text-sm font-semibold transition-colors ${days === n ? "border-[#0B6FC2] bg-[#0B6FC2] text-white" : "bg-white hover:bg-slate-50"}`} data-testid={`folha-days-${n}`}>{n}</button>))}
        <input type="number" min={1} max={60} value={days} onChange={(e) => setDays(Math.round(Number(e.target.value)) || 0)} className="h-10 w-20 rounded-lg border px-2 text-sm" data-testid="folha-days-input" />
        <span className="text-sm text-slate-600">dia(s) úteis</span></div>
      <p className="text-sm" data-testid="folha-due-preview">{due ? <>Previsão de devolução: <b>{dateBR(due)}</b></> : "Informe de 1 a 60 dias úteis."}</p>
      <p className="text-[11px] text-slate-500">Conta a partir de hoje, sem finais de semana e feriados do calendário configurado.</p>
      <Button className="h-11 w-full font-bold bg-[#0B6FC2] hover:bg-[#095a9e]" disabled={busy || !due} onClick={start} data-testid="folha-start-btn"><Play className="mr-1.5 h-4 w-4" />INICIAR PROCESSAMENTO</Button>
    </section>);
}

export function ReturnPanel({ r, returns, onDone }) {
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [msg, setMsg] = useState("");
  const upload = async (files) => {
    if (!files.length) return;
    const fd = new FormData(); files.forEach((f) => fd.append("files", f));
    setBusy(true);
    try { await api.post(`/folha/requests/${r.id}/returns`, fd); toast.success(`${files.length} arquivo(s) anexado(s)`); onDone(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const remove = async (d) => { try { await api.delete(`/folha/requests/${r.id}/returns/${d.id}`); onDone(); } catch (e) { toast.error(errMsg(e)); } };
  const finish = async () => {
    setBusy(true);
    try { await api.post(`/folha/requests/${r.id}/finish`, { message: msg }); toast.success("Folha processada e enviada ao cliente com sucesso."); setConfirm(false); onDone(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <section className="space-y-3 rounded-xl border border-emerald-200 bg-emerald-50/40 p-4" data-testid="folha-return-panel">
      <div className="label-xs">Folha processada</div>
      <label className={`flex h-11 cursor-pointer items-center justify-center gap-1.5 rounded-lg border-2 border-dashed bg-white text-sm font-semibold text-[#0B6FC2] ${busy ? "opacity-50" : "hover:bg-slate-50"}`}>
        <Upload className="h-4 w-4" />+ ANEXAR ARQUIVOS
        <input type="file" multiple className="hidden" disabled={busy} accept=".pdf,.png,.jpg,.jpeg,.xls,.xlsx,.csv,.doc,.docx,.txt,.zip" onChange={(e) => { upload([...e.target.files]); e.target.value = ""; }} data-testid="folha-return-input" /></label>
      {returns.length ? <div className="space-y-1.5" data-testid="folha-return-list">{returns.map((d) => fileLine(d, remove))}</div>
        : <p className="text-xs text-slate-500" data-testid="folha-return-empty">Nenhum arquivo de retorno anexado. Anexe a folha processada, contracheques, relatórios ou guias.</p>}
      <Button className="h-11 w-full font-bold" disabled={busy || !returns.length} onClick={() => setConfirm(true)} data-testid="folha-finish-btn"><Send className="mr-1.5 h-4 w-4" />CONCLUIR E ENVIAR AO CLIENTE</Button>
      <Dialog open={confirm} onOpenChange={setConfirm}>
        <DialogContent data-testid="folha-finish-dialog">
          <DialogHeader><DialogTitle>Concluir e enviar ao cliente?</DialogTitle>
            <DialogDescription>{returns.length} arquivo(s) de retorno serão disponibilizados ao cliente e a folha ficará como CONCLUÍDA.</DialogDescription></DialogHeader>
          <Textarea rows={3} value={msg} onChange={(e) => setMsg(e.target.value)} placeholder="Mensagem ao cliente (opcional)" data-testid="folha-finish-message" />
          <DialogFooter><Button variant="outline" onClick={() => setConfirm(false)}>Voltar</Button>
            <Button disabled={busy} onClick={finish} data-testid="folha-finish-confirm">{busy ? "Enviando..." : "Sim, concluir e enviar"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </section>);
}
